import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ChevronLeft,
  ListChecks,
  Pencil,
  PlayCircle,
  Plus,
  Shield,
  Trash2,
  Upload,
} from 'lucide-react';
import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ContractStatusBadge } from '@/components/ui/ContractStatusBadge';
import { Dialog } from '@/components/ui/Dialog';
import { Tabs } from '@/components/ui/Tabs';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { SystemName } from '@/components/ui/SystemName';
import { ApiError } from '@/lib/api';
import {
  useCustomer,
  useCustomerStats,
  useDeleteCustomer,
  useRestoreCustomer,
} from '@/hooks/useCustomers';
import { useSystems } from '@/hooks/useSystems';
import { SystemFormDialog } from '@/components/systems/SystemFormDialog';
import type { SystemFormDialogState } from '@/components/systems/SystemFormDialog';
import { formatDateTime, policyLabel } from '@/lib/format';
import type { System } from '@/types/api';
import { ContractsTab } from '@/components/customers/ContractsTab';
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog';
import { LocationsCard } from '@/components/customers/LocationsCard';
import { MaintenancePlanTab } from '@/components/customers/MaintenancePlanTab';
import { MaintenanceHistoryCard } from '@/components/customers/MaintenanceHistoryCard';
import { MaintenanceSessionCard } from '@/components/customers/MaintenanceSessionCard';
import { MaintenanceWindowTab } from '@/components/customers/MaintenanceWindowTab';
import { CustomerContactsTab } from '@/components/customers/CustomerContactsTab';
import { HnfIntegrationCard } from '@/components/customers/HnfIntegrationCard';
import { ContractReconciliationTab } from '@/components/customers/ContractReconciliationTab';

/**
 * Customer-Detail (Sprint 2 Update von WT-102/203/205/206).
 *
 * Über den Tabs sitzt die Wartungsfenster-Karte: ein Fenster pro Kunde,
 * das den Kundenmails den Takt gibt (auf/zu jederzeit von Hand).
 *
 * Sub-Tabs:
 *  - overview     ✅ Stammdaten + IN-Snippet + Stats + HNF-Karte
 *  - locations    ✅ Tabelle aller customer_locations (Sprint 1)
 *  - systems      ✅ Reuse Systems-API mit ?filter[customer_id]=... (Sprint 1)
 *  - contracts    ✅ Sprint 2 (WT-203, Recurring-Invoice + Service-Profil)
 *  - reconciliation ✅ Vertragsabgleich: Position <-> System (Fall Fortis Unum)
 *  - plan         ✅ Sprint 2 (WT-204, Wartungsplan-Generator)
 *  - window       ✅ Sprint 2 (WT-205, Wartungsfenster pro Kunde)
 *  - notifications ✅ Kontakte & Mails (IN + manuell, Opt-out)
 *  - audit        ✅ Verweis auf zentrales Audit-Log (/settings/audit)
 */

const TABS = [
  'overview',
  'locations',
  'systems',
  'contracts',
  'reconciliation',
  'plan',
  'window',
  'notifications',
  'audit',
] as const;
type SubTab = (typeof TABS)[number];

const searchSchema = z.object({
  tab: z.enum(TABS).optional(),
});

export const Route = createFileRoute('/customers/$id')({
  component: CustomerDetailPage,
  validateSearch: (s) => searchSchema.parse(s),
});

function CustomerDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const { tab = 'overview' } = Route.useSearch();
  const navigate = useNavigate({ from: '/customers/$id' });

  const customerQuery = useCustomer(id);
  const archiviert = Boolean(customerQuery.data?.deactivated_at);
  // GET /customers/{id}/stats weist archivierte Kunden mit 404 ab. Die
  // Abfrage deshalb gar nicht erst starten - sonst steht im Übersichts-Tab
  // ein roter Kasten "Kunde <uuid> nicht gefunden" unter dem gelben Banner,
  // in dem der Name desselben Kunden steht (Befund 10.09.2026, zweite Runde).
  // Erst fragen, wenn der Kunde da ist: sonst läuft die Abfrage schon im
  // ersten Render los, bevor ``archiviert`` überhaupt bekannt ist - und der
  // 404 steht als Fehlerzustand im Cache.
  const statsQuery = useCustomerStats(
    customerQuery.data !== undefined && !archiviert ? id : undefined
  );
  const deleteMutation = useDeleteCustomer();
  const restoreMutation = useRestoreCustomer();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);
  const [systemForm, setSystemForm] = useState<SystemFormDialogState | null>(null);

  const openCreateSystem = (): void => setSystemForm({ mode: 'create', customerId: id });

  // Geführter Vorschlag (Spec S8-06 §5b): Vertrag gemappt, aber noch kein
  // System erfasst. Reine Empfehlung — Verträge erzwingen nichts (Regel 1).
  // Bei archivierten Kunden unterdrückt: dort darf nichts mehr angelegt werden.
  const showNoSystemsHint =
    !archiviert &&
    statsQuery.data?.active_contract_status === 'mapped' &&
    statsQuery.data.systems_count === 0;

  const setTab = (next: SubTab): void => {
    void navigate({ search: { tab: next === 'overview' ? undefined : next } });
  };

  // Reiter, die es am archivierten Kunden nicht gibt: ihre Endpunkte
  // antworten dort mit 404 (Standorte, Wartungsplan, Vertragsabgleich) oder
  // sie bieten ein "Speichern" an, das der Server ablehnt (Zeitfenster ->
  // PATCH /customers). Ein Reiter, der nur einen roten Kasten zeigen kann,
  // gehört nicht in die Leiste - der Deep-Link darauf fällt still auf die
  // Übersicht zurück.
  const gesperrteReiter: SubTab[] = ['locations', 'reconciliation', 'plan', 'window'];
  const reiterSichtbar = (kandidat: SubTab): boolean =>
    !archiviert || !gesperrteReiter.includes(kandidat);
  const aktiverTab: SubTab = reiterSichtbar(tab) ? tab : 'overview';

  const runDelete = (): void => {
    deleteMutation.mutate(
      { customerId: id },
      {
        onSuccess: () => {
          setConfirmDelete(false);
          void navigate({ to: '/customers' });
        },
      }
    );
  };

  const runHardDelete = (): void => {
    deleteMutation.mutate(
      { customerId: id, hard: true },
      {
        onSuccess: () => {
          setConfirmHardDelete(false);
          void navigate({ to: '/customers' });
        },
      }
    );
  };

  const hardDeleteBlockers = blockersFromError(deleteMutation.error);

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/customers"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ChevronLeft className="size-4" />
        Zurück zur Liste
      </Link>

      {customerQuery.isLoading && <SkeletonRows rows={3} cols={3} />}
      {customerQuery.isError && <ErrorAlert error={customerQuery.error} />}

      {customerQuery.data && (
        <>
          {archiviert && (
            <div
              role="status"
              data-testid="customer-archived-banner"
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/50 bg-warning/10 p-3"
            >
              <div>
                <p className="font-medium">
                  Archiviert am {formatDateTime(customerQuery.data.deactivated_at)}
                </p>
                <p className="text-sm text-muted">
                  Der Kunde ist aus allen Listen ausgeblendet, seine Verträge sind abgehängt und
                  die Invoice-Ninja-Verknüpfung ist freigegeben. Bearbeiten ist gesperrt.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => restoreMutation.mutate(id)}
                  loading={restoreMutation.isPending}
                  data-testid="customer-restore"
                >
                  Wiederherstellen
                </Button>
                <Button
                  variant="ghost"
                  className="text-danger"
                  onClick={() => {
                    deleteMutation.reset();
                    setConfirmHardDelete(true);
                  }}
                  data-testid="customer-hard-delete"
                >
                  Endgültig löschen
                </Button>
              </div>
            </div>
          )}
          {archiviert && restoreMutation.isError && <ErrorAlert error={restoreMutation.error} />}

          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold">{customerQuery.data.name}</h1>
              <p className="text-muted">
                <span className="font-mono">{customerQuery.data.slug}</span>
                {archiviert && (
                  <Badge tone="warning" className="ml-2">
                    archiviert
                  </Badge>
                )}
              </p>
            </div>
            {!archiviert && (
              <div className="flex flex-wrap gap-2">
                {/* Zwei getrennte Wege, bewusst nicht ein Knopf mit zwei
                    Bedeutungen: "Alles warten" führt in den Komplettmodus
                    (pro passender Routine ein Lauf), "Einzelne Routine" bleibt
                    der bisherige Wizard mit vorausgewählten Systemen.
                    An einem archivierten Kunden darf nichts mehr passieren,
                    was Wartung auslöst oder Stammdaten ändert — deshalb
                    ist diese Leiste dort komplett weg. */}
                <Link to="/runs/new" search={{ customers: customerQuery.data.id, alle: true }}>
                  <Button
                    leftIcon={<ListChecks className="size-4" />}
                    data-testid="customer-start-all"
                  >
                    Alles warten
                  </Button>
                </Link>
                <Link to="/runs/new" search={{ customers: customerQuery.data.id }}>
                  <Button
                    variant="secondary"
                    leftIcon={<PlayCircle className="size-4" />}
                    data-testid="customer-start-run"
                  >
                    Einzelne Routine
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  leftIcon={<Pencil className="size-4" />}
                  onClick={() => setEditOpen(true)}
                  data-testid="customer-edit"
                >
                  Bearbeiten
                </Button>
                <Button
                  variant="ghost"
                  className="text-danger"
                  leftIcon={<Trash2 className="size-4" />}
                  onClick={() => {
                    deleteMutation.reset();
                    setConfirmDelete(true);
                  }}
                >
                  Löschen
                </Button>
              </div>
            )}
          </header>

          {showNoSystemsHint && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm"
              data-testid="contract-no-systems-banner"
            >
              <p>
                <span className="font-medium">Vertrag vorhanden, aber keine Systeme.</span> Systeme
                anlegen oder Inventar importieren — nur ein Vorschlag, keine Pflicht.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  leftIcon={<Plus className="size-4" />}
                  onClick={openCreateSystem}
                  data-testid="banner-create-system"
                >
                  System anlegen
                </Button>
                <Link to="/inventory/import">
                  <Button size="sm" variant="secondary" leftIcon={<Upload className="size-4" />}>
                    Inventar importieren
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Bewusst über den Tabs: der Fenster-Zustand entscheidet, ob der
              Kunde Mails bekommt — das darf nicht in einem Tab verschwinden.
              Am archivierten Kunden entfällt die Karte: GET
              /customers/{id}/maintenance-session antwortet dort mit 404, und
              unter dem gelben Banner "Bearbeiten ist gesperrt" stand ein roter
              Fehlkasten "Kunde <uuid> nicht gefunden" — für einen Kunden,
              dessen Name darübersteht — plus ein "Erneut laden", das nie
              funktionieren konnte. */}
          {archiviert ? (
            <p className="text-sm text-muted" data-testid="archiv-kein-wartungsfenster">
              Archivierter Kunde — es gibt kein Wartungsfenster und keine
              Nacherfassung. Zuerst wiederherstellen.
            </p>
          ) : (
            <MaintenanceSessionCard customerId={id} />
          )}

          {/* Direkt darunter statt in einem Tab: die häufigste Frage zu einem
              Kunden ist "wann war die letzte Wartung und was lief da?". Ein
              eigener Tab hieße außerdem zwangsläufig wieder
              "Wartungsfenster" — den Namen tragen auf dieser Seite schon der
              Zeitfenster-Tab und die Karte darüber. */}
          <MaintenanceHistoryCard customerId={id} readOnly={archiviert} />

          <CustomerFormDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            customer={customerQuery.data}
          />

          <SystemFormDialog
            state={systemForm}
            onClose={() => setSystemForm(null)}
            onSuccess={() => setTab('systems')}
          />

          <Dialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            title="Kunde archivieren?"
            description={customerQuery.data.name}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleteMutation.isPending}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="danger"
                  onClick={runDelete}
                  loading={deleteMutation.isPending}
                  data-testid="customer-confirm-delete"
                >
                  Archivieren
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-2 text-sm">
              <p>
                Der Kunde wird archiviert: Verträge werden abgehängt und tauchen wieder unter
                &quot;unverknüpft&quot; auf, die Invoice-Ninja-Verknüpfung wird freigegeben,
                Wartungspläne werden stillgelegt. Systeme, Läufe und Historie bleiben.
                Wiederherstellen ist jederzeit möglich.
              </p>
              {deleteMutation.isError && <ErrorAlert error={deleteMutation.error} />}
            </div>
          </Dialog>

          <Dialog
            open={confirmHardDelete}
            onClose={() => setConfirmHardDelete(false)}
            title="Kunde endgültig löschen?"
            description={customerQuery.data.name}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmHardDelete(false)}
                  disabled={deleteMutation.isPending}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="danger"
                  onClick={runHardDelete}
                  loading={deleteMutation.isPending}
                  data-testid="customer-confirm-hard-delete"
                >
                  Endgültig löschen
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-2 text-sm">
              <p>
                Der Kunde und alle abgeleiteten Daten (Standorte, Service-Profile, Wartungspläne)
                werden unwiderruflich entfernt. Verträge aus Invoice Ninja bleiben bestehen und
                verlieren nur die Zuordnung.
              </p>
              {deleteMutation.isError && (
                <>
                  <ErrorAlert error={deleteMutation.error} />
                  {hardDeleteBlockers.length > 0 && (
                    <ul data-testid="hard-delete-blockers" className="list-disc pl-5">
                      {hardDeleteBlockers.map((b) => (
                        <li key={b.table}>
                          {b.label}: {b.count}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </Dialog>

          <Tabs<SubTab>
            ariaLabel="Detail-Bereiche"
            active={aktiverTab}
            onChange={setTab}
            items={([
              { id: 'overview', label: 'Übersicht' },
              { id: 'locations', label: 'Standorte' },
              { id: 'systems', label: 'Systeme' },
              { id: 'contracts', label: 'Verträge' },
              { id: 'reconciliation', label: 'Vertragsabgleich' },
              { id: 'plan', label: 'Wartungsplan' },
              { id: 'window', label: 'Wartungsfenster' },
              { id: 'notifications', label: 'Kontakte & Mails' },
              { id: 'audit', label: 'Audit' },
            ] as { id: SubTab; label: string }[]).filter((eintrag) =>
              reiterSichtbar(eintrag.id)
            )}
          >
            {aktiverTab === 'overview' && (
              <OverviewTab customer={customerQuery.data} archiviert={archiviert} />
            )}
            {aktiverTab === 'locations' && <LocationsCard customerId={id} />}
            {aktiverTab === 'systems' && (
              <SystemsTab customerId={id} onCreateSystem={openCreateSystem} />
            )}
            {aktiverTab === 'contracts' && <ContractsTab customerId={id} />}
            {aktiverTab === 'reconciliation' && <ContractReconciliationTab customerId={id} />}
            {aktiverTab === 'plan' && <MaintenancePlanTab customerId={id} />}
            {aktiverTab === 'window' && <MaintenanceWindowTab customer={customerQuery.data} />}
            {aktiverTab === 'notifications' && (
              <CustomerContactsTab customerId={id} readOnly={archiviert} />
            )}
            {aktiverTab === 'audit' && <AuditTab customerName={customerQuery.data.name} />}
          </Tabs>
          {archiviert && (
            <p className="text-sm text-muted" data-testid="archiv-gesperrte-reiter">
              Standorte, Vertragsabgleich, Wartungsplan und Zeitfenster stehen erst nach
              dem Wiederherstellen wieder zur Verfügung.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function OverviewTab({
  customer,
  archiviert,
}: {
  customer: NonNullable<ReturnType<typeof useCustomer>['data']>;
  archiviert: boolean;
}): JSX.Element {
  // Am archivierten Kunden gar nicht erst fragen: /stats antwortet mit 404
  // (siehe Kommentar oben an der Seiten-Abfrage).
  const stats = useCustomerStats(archiviert ? undefined : customer.id);
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="Stammdaten">
        <dl className="grid grid-cols-3 gap-2 text-sm">
          <dt className="text-muted">Name</dt>
          <dd className="col-span-2">{customer.name}</dd>
          <dt className="text-muted">Firmenname</dt>
          <dd className="col-span-2">{customer.legal_name ?? '–'}</dd>
          <dt className="text-muted">Slug</dt>
          <dd className="col-span-2 font-mono">{customer.slug}</dd>
          <dt className="text-muted">Public-Slug</dt>
          <dd className="col-span-2 font-mono">{customer.public_slug ?? '–'}</dd>
          <dt className="text-muted">Kontakt</dt>
          <dd className="col-span-2">
            {customer.contact_email ?? '–'}
            {customer.contact_phone && (
              <span className="ml-2 text-muted">{customer.contact_phone}</span>
            )}
          </dd>
          {/* Der Kundenstandard entscheidet, ob der Client nachts für die
              Systeme dieses Kunden einen Tunnel aufbaut - er stand bisher nur
              im Bearbeiten-Dialog, und wer wissen wollte, ob ein System einen
              Tunnel bekommt, musste zwei Masken öffnen. */}
          <dt className="text-muted">VPN-Profil (Standard)</dt>
          <dd className="col-span-2 font-mono">
            {customer.vpn_profile ?? '–'}
            {!customer.vpn_profile && (
              <span className="ml-2 font-sans text-muted">
                kein Tunnel für Systeme ohne eigenen Override
              </span>
            )}
          </dd>
          <dt className="text-muted">Mail-Empfänger</dt>
          <dd className="col-span-2">
            {customer.notification_emails && customer.notification_emails.length > 0
              ? customer.notification_emails.join(', ')
              : '–'}
          </dd>
          <dt className="text-muted">Wartungsfenster</dt>
          <dd className="col-span-2">
            <Badge tone="primary">{customer.maintenance_window ?? 'standard'}</Badge>
          </dd>
          <dt className="text-muted">Frequenz</dt>
          <dd className="col-span-2">{customer.maintenance_frequency ?? 'monthly'}</dd>
          <dt className="text-muted">Notizen</dt>
          <dd className="col-span-2 whitespace-pre-wrap">{customer.notes ?? '–'}</dd>
          <dt className="text-muted">Erstellt</dt>
          <dd className="col-span-2 text-muted">{formatDateTime(customer.created_at)}</dd>
          <dt className="text-muted">Aktualisiert</dt>
          <dd className="col-span-2 text-muted">{formatDateTime(customer.updated_at)}</dd>
        </dl>
      </Card>

      <div className="flex flex-col gap-4">
        <Card title="Stats" description="Aggregiert via /customers/{id}/stats">
          {archiviert && (
            <p className="text-sm text-muted" data-testid="archiv-keine-stats">
              Archivierter Kunde — keine Aggregation. Zuerst wiederherstellen.
            </p>
          )}
          {stats.isLoading && !archiviert && <p className="text-sm text-muted">Lädt …</p>}
          {stats.isError && !archiviert && <ErrorAlert error={stats.error} />}
          {stats.data && (
            <dl className="grid grid-cols-3 gap-2 text-sm">
              <dt className="text-muted">Standorte</dt>
              <dd className="col-span-2">{stats.data.locations_count}</dd>
              <dt className="text-muted">Systeme</dt>
              <dd className="col-span-2">
                {stats.data.systems_count}
                {stats.data.active_systems_count != null && (
                  <span className="ml-1 text-muted">({stats.data.active_systems_count} aktiv)</span>
                )}
              </dd>
              <dt className="text-muted">Letzte Wartung</dt>
              <dd className="col-span-2">{formatDateTime(stats.data.last_maintenance_at)}</dd>
              <dt className="text-muted">Vertrag</dt>
              <dd className="col-span-2">
                <ContractStatusBadge status={stats.data.active_contract_status} />
              </dd>
            </dl>
          )}
        </Card>

        <Card title="Invoice-Ninja-Verknüpfung" description="Recurring-Invoice-Sync">
          {customer.invoice_ninja_client_id ? (
            <div className="flex flex-col gap-3 text-sm">
              <dl className="grid grid-cols-3 gap-2">
                <dt className="text-muted">Kundenname</dt>
                <dd className="col-span-2">{customer.invoice_ninja_client_name ?? '–'}</dd>
                <dt className="text-muted">Kundennummer</dt>
                <dd className="col-span-2">{customer.invoice_ninja_number ?? '–'}</dd>
                <dt className="text-muted">Client-Id</dt>
                <dd className="col-span-2 font-mono">{customer.invoice_ninja_client_id}</dd>
                <dt className="text-muted">Letzter Sync</dt>
                <dd className="col-span-2">
                  {customer.invoice_ninja_synced_at
                    ? formatDateTime(customer.invoice_ninja_synced_at)
                    : 'noch nie synchronisiert'}
                </dd>
              </dl>
              <div className="flex flex-col gap-1">
                <Link
                  to="/customers/$id"
                  params={{ id: customer.id }}
                  search={{ tab: 'contracts' }}
                  className="text-primary hover:underline"
                >
                  Vertrags-Tab öffnen
                </Link>
                <a
                  href={`https://office.itkaufmann.at/#/clients/${customer.invoice_ninja_client_id}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-primary hover:underline"
                >
                  In InvoiceNinja öffnen
                </a>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
              <p>
                Dieser Kunde hängt an keinem InvoiceNinja-Client. Adressen und Kontakte müssen
                hier von Hand gepflegt werden, Änderungen in InvoiceNinja kommen nicht an.
              </p>
              <Link
                to="/settings/contracts/mapping"
                className="mt-2 inline-block text-primary hover:underline"
              >
                Kunden zuordnen
              </Link>
            </div>
          )}
        </Card>

        <HnfIntegrationCard customerId={customer.id} />
      </div>
    </div>
  );
}

function SystemsTab({
  customerId,
  onCreateSystem,
}: {
  customerId: string;
  onCreateSystem: () => void;
}): JSX.Element {
  const { data, isLoading, isError, error } = useSystems({ customerId, limit: 200 });
  if (isLoading) return <SkeletonRows rows={5} cols={5} />;
  if (isError) return <ErrorAlert error={error} />;
  if (!data || data.data.length === 0) {
    return (
      <EmptyState
        title="Keine Systeme"
        description="Für diesen Kunden ist noch kein wartbares System angelegt."
        actions={
          <>
            <Button
              leftIcon={<Plus className="size-4" />}
              onClick={onCreateSystem}
              data-testid="systems-tab-create-system"
            >
              System anlegen
            </Button>
            <Link to="/inventory/import">
              <Button variant="secondary" leftIcon={<Upload className="size-4" />}>
                Inventar importieren
              </Button>
            </Link>
          </>
        }
      />
    );
  }
  return (
    <DataTable
      caption="Systeme des Kunden"
      columns={KUNDE_SYSTEME_COLUMNS}
      rows={data.data}
      rowKey={(sys) => sys.id}
      storageKey="kunde-systeme"
      rowMuted={(sys) => sys.scope !== 'in_scope'}
    />
  );
}

function ohneStrich(text: string): string {
  return text === '–' ? '' : text;
}

const KUNDE_SYSTEME_COLUMNS: ReadonlyArray<Column<System>> = [
  {
    id: 'hostname',
    header: 'Hostname',
    filterValue: (sys) => sys.hostname,
    cell: (sys) => <SystemName system={sys} asLink />,
  },
  {
    id: 'rolle',
    header: 'Rolle',
    filter: 'select',
    filterValue: (sys) => sys.role ?? '',
    cell: (sys) => <Badge tone="neutral">{sys.role}</Badge>,
  },
  {
    id: 'primaere-ip',
    header: 'Primäre IP',
    className: 'font-mono text-xs',
    filterValue: (sys) => sys.primary_ip ?? '',
    cell: (sys) => sys.primary_ip ?? '–',
  },
  {
    id: 'os',
    header: 'OS',
    className: 'text-muted',
    filter: 'select',
    filterValue: (sys) => sys.os_family ?? '',
    cell: (sys) => sys.os_family ?? '–',
  },
  {
    id: 'update-policy',
    header: 'Update-Policy',
    filter: 'select',
    filterValue: (sys) => policyLabel(sys.update_policy),
    cell: (sys) => (
      <Badge tone={updatePolicyTone(sys.update_policy)}>
        {policyLabel(sys.update_policy)}
      </Badge>
    ),
  },
  {
    id: 'letzte-wartung',
    header: 'Letzte Wartung',
    className: 'text-muted',
    filterValue: (sys) => ohneStrich(formatDateTime(sys.last_maintained_at)),
    cell: (sys) => formatDateTime(sys.last_maintained_at),
  },
];

function AuditTab({ customerName }: { customerName: string }): JSX.Element {
  return (
    <Card>
      <EmptyState
        icon={<Shield className="size-8" />}
        title="Audit-Trail"
        description={`Alle protokollierten Änderungen (inkl. der Aktionen zu ${customerName}) liegen im zentralen, hash-verketteten Audit-Log.`}
        actions={
          <Link to="/settings/audit">
            <Button leftIcon={<Shield className="size-4" />}>Audit-Log öffnen</Button>
          </Link>
        }
      />
    </Card>
  );
}

interface HardDeleteBlocker {
  table: string;
  label: string;
  count: number;
}

/**
 * Blocker aus der 409-Antwort des endgültigen Löschens.
 * `ApiError.problem` ist auf die RFC-7807-Pflichtfelder typisiert; die
 * Zusatzfelder des Backends liegen nur im ungetypten `raw`.
 */
function blockersFromError(error: unknown): HardDeleteBlocker[] {
  if (!(error instanceof ApiError)) return [];
  const raw = error.raw as { blockers?: unknown } | null;
  if (!raw || !Array.isArray(raw.blockers)) return [];
  return raw.blockers.filter(
    (b): b is HardDeleteBlocker =>
      typeof b === 'object' &&
      b !== null &&
      typeof (b as HardDeleteBlocker).label === 'string' &&
      typeof (b as HardDeleteBlocker).count === 'number'
  );
}

function updatePolicyTone(
  p: string | null | undefined
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (p) {
    case 'yes':
    case 'auto':
      return 'success';
    case 'manual':
      return 'warning';
    case 'excluded':
    case 'never':
    case 'no':
      return 'danger';
    default:
      return 'neutral';
  }
}
